import type { TourStep } from 'types'

export const isElementVisible = (selector: string): boolean => {
  let el: Element | null
  try {
    el = document.querySelector(selector)
  } catch {
    // Older browsers throw a SyntaxError on selectors they don't support
    // (e.g. CSS :has() before Firefox 121). Treat as not visible rather
    // than letting the error crash the page.
    return false
  }
  if (!el) return false
  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false
  return el.isConnected
}

type Phase = 'new' | 'returning';

type TourState = {
  stepsArr: TourStep[];
  phase: Phase;
  unviewedReturning: TourStep[];
};

export const computeTourState = (
  {
    featureTour,
    steps,
    run,
  }: {
    featureTour: any | null | undefined;
    steps: TourStep[];
    run: boolean | null | undefined;
  },
): TourState => {
  const stepsWithStatus = steps.map(step => ({
    ...step,
    status: setStepStatus({ featureTour, step }),
    skipBeacon: !run,
  }))

  const phase = derivePhase(stepsWithStatus)

  return {
    stepsArr: filterByAudience(stepsWithStatus, phase),
    phase,
    unviewedReturning: stepsWithStatus.filter(s => s.audience === 'returning' && !s.status),
  }
}

const setStepStatus = ({
  featureTour,
  step,
}: {
  featureTour: any | null | undefined;
  step: TourStep;
}) => {
  if (!featureTour) return ''

  return featureTour[step['name']]
}

const derivePhase = (steps: TourStep[]): Phase => {
  const hasNewSteps = steps.some(s => s.audience === 'new')
  if (!hasNewSteps) return 'returning'

  const hasActivity = steps.some(s => s.audience === 'new' && Boolean(s.status))
  return hasActivity ? 'returning' : 'new'
}

const filterByAudience = (steps: TourStep[], phase: Phase): TourStep[] => {
  // Returning users see all audiences; joyride filters by per-step status.
  if (phase === 'returning') return steps
  return steps.filter(s => s.audience === 'new')
}

export const tourAlertViewed = ({
  featureTour,
}: {
  featureTour: string | null | undefined | any;
}) => {
  if (!featureTour) return false

  if (typeof featureTour === 'string') return true

  if (typeof featureTour === 'object') return Object.values(featureTour).some(value => value)
}
